<template>
  <div :style="rehabMerchantThemeStyle">
    <base-title-bar-scroll-page ref="container" :show-title-bar="!isApp" :title="detailTitle">
      <div class="detail" v-if="!loading && course">
        <div v-if="!currentVideoUrl" class="detail__cover" :style="{ backgroundImage: 'url(' + (course.coverUrl || course.cover || '') + ')' }">
          <div class="detail__badge" :class="course.vip ? 'detail__badge--vip' : 'detail__badge--free'">{{ course.vip ? 'vip会员课程' : '免费' }}</div>
          <div class="detail__title">{{ course.title }}</div>
        </div>
        <div v-else class="detail__player" ref="playerContainer" :style="playerStickyStyle">
          <video :poster="currentCoverUrl || course.coverUrl || course.cover || ''"
                 :src="currentVideoUrl"
                 controls autoplay
                 ref="player"
                 @timeupdate="onTimeUpdate"
                 @ended="onEnded"
                 style="width: 100%; max-height: 60vh"></video>
        </div>

        <div class="detail__body">
          <div class="detail__meta">
            <span v-if="course.type" class="tag">{{ course.type }}</span>
            <span class="tag" :class="course.vip ? 'tag--vip' : 'tag--free'">{{ course.vip ? 'vip会员' : '免费' }}</span>
          </div>

          <div class="detail__course-title">{{ course.title }}</div>
          <div class="detail__brief" v-if="course.briefText">{{ course.briefText }}</div>

          <van-tabs v-model="activeTab" :color="rehabMerchantConfig.themeColor" :title-active-color="rehabMerchantConfig.themeColor" swipeable sticky>
            <van-tab title="课程详情" name="detail">
              <div class="detail__section" v-if="hasIntro()">
                <div class="detail__rich" v-html="course.intro"></div>
              </div>
              <div v-else class="detail__empty">暂无详情</div>
            </van-tab>
            <van-tab title="课程目录" name="catalog">
              <div class="detail__section" v-if="hasCatalog()">
                <div class="catalog">
                  <div 
                    class="catalog__item" 
                    :class="{ 
                      'is-active': v.videoId === currentVideoId,
                      'is-unlocked': course.vip && userVipStatus === true && v.videoId !== currentVideoId,
                      'is-trial': course.vip && userVipStatus !== true && isFirstVideo(v) && v.videoId !== currentVideoId,
                      'is-locked': course.vip && userVipStatus !== true && !isFirstVideo(v)
                    }" 
                    v-for="(v, index) in course.courseVideoList" 
                    :key="v.videoId" 
                    @click="selectVideo(v)"
                  >
                    <div class="catalog__content">
                      <div class="catalog__title">
                        <span class="catalog__title-text">{{ v.title }}</span>
                        <!-- VIP课程标识 -->
                        <div class="catalog__badges">
                          <template v-if="course.vip">
                            <!-- 已开通会员：显示VIP已解锁 -->
                            <span v-if="userVipStatus === true" class="catalog__badge catalog__badge--unlocked">VIP已解锁</span>
                            <!-- 未开通会员且是第一个视频：显示试看 -->
                            <span v-else-if="isFirstVideo(v)" class="catalog__badge catalog__badge--trial">试看</span>
                            <!-- 未开通会员且不是第一个视频：显示锁定图标 -->
                            <van-icon 
                              v-else
                              name="lock" 
                              size="14" 
                              color="#999" 
                              class="catalog__lock"
                            />
                          </template>
                        </div>
                      </div>
                      <div class="catalog__meta">时长：{{ formatDuration(v.duration) }}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="detail__empty">暂无目录</div>
            </van-tab>
          </van-tabs>

          <div class="detail__actions"></div>
        </div>
      </div>
      <div v-else style="padding: 16px"></div>

      <!-- 底部固定操作栏 -->
      <div class="detail__bottom-bar" v-if="course">
        <!-- 未播放：显示开始学习 -->
        <template v-if="!currentVideoUrl">
          <div class="bottom-bar__left">
            <div class="bottom-bar__title">
              <span v-if="!course.vip">免费课程</span>
              <span v-else-if="userVipStatus === true">VIP会员课程</span>
              <span v-else>会员课程</span>
            </div>
            <div class="bottom-bar__desc" v-if="course.type">{{ course.type }}</div>
          </div>
          <div class="bottom-bar__right">
            <van-button 
              round 
              type="primary" 
              color="var(--rehab-theme-color, #07c160)" 
              @click="goPlay"
              :disabled="startButtonDisabled"
            >
              {{ startButtonText }}
            </van-button>
          </div>
        </template>

        <!-- 播放中：显示上一节/下一节 -->
        <template v-else>
          <div class="bottom-bar__left">
            <div class="bottom-bar__title">
              <span v-if="isTrialMode">免费试看</span>
              <span v-else-if="course.vip && userVipStatus === true">VIP正在播放</span>
              <span v-else>正在播放</span>
            </div>
            <div class="bottom-bar__desc">{{ currentVideoTitle || course.title }}</div>
          </div>
          <div class="bottom-bar__right bottom-bar__nav">
            <van-button 
              class="nav-btn" 
              round 
              @click="playPrev" 
              :disabled="!hasPrev"
              :type="isTrialMode && hasPrev ? 'primary' : 'default'"
              :color="isTrialMode && hasPrev ? rehabMerchantConfig.themeColor : undefined"
            >
              {{ prevButtonText }}
            </van-button>
            <van-button 
              class="nav-btn nav-btn--primary" 
              round 
              type="primary" 
              color="var(--rehab-theme-color, #07c160)" 
              @click="playNext" 
              :disabled="!isTrialMode && !hasNext"
            >
              {{ nextButtonText }}
            </van-button>
          </div>
        </template>
      </div>
    </base-title-bar-scroll-page>

    <!-- VIP购买弹窗 -->
    <vip-purchase-popup 
      :show="showVipPopup" 
      :merchant="rehabMerchantState.queryMerchant || rehabMerchantConfig.key"
      @close="showVipPopup = false"
      @purchase="onVipPurchase"
    />
  </div>
  
</template>
<script>
import BaseTitleBarScrollPage from '@/h5/doucan/components/BaseTitleBarScrollPage.vue'
import VipPurchasePopup from '@/h5/doucan/components/VipPurchasePopup.vue'
import TicaoDao from '@/h5/doucan/network/Ticao.js'
import VipDao from '@/h5/doucan/network/VipDao.js'
import rehabMerchantMixin from './rehabMerchantMixin'
import { getMerchantCoursesFromMarkdown, saveMerchantCourseHistory } from './merchantCourseSource'

export default {
  name: 'RehabCourseDetail',
  mixins: [rehabMerchantMixin],
  components: { 
    BaseTitleBarScrollPage,
    VipPurchasePopup
  },
  data () {
    return {
      isApp: false,
      courseId: null,
      course: null,
      loading: true,
      activeTab: 'detail',
      currentVideoId: null,
      currentVideoUrl: '',
      currentVideoTitle: '',
      currentCoverUrl: '',
      // 播放上报相关
      lastReportSecond: 0,
      lastReportTs: 0,
      reportIntervalSec: 5,
      // VIP购买弹窗
      showVipPopup: false,
      // 用户VIP状态
      userVipStatus: null
    }
  },
  mounted () {
    const q = this.$route.query || {}
    this.courseId = q.id || q.courseId
    this.isApp = q.isApp === '1' || q.isApp === 'true' || q.isapp === '1' || q.isapp === 'true'
    this.initRehabMerchantFromRoute()
    this.applyRehabMerchantTheme()
    this.loadDetail()
    this.hideLoading()
    // 页面关闭/刷新前补报
    this._beforeUnload = () => {
      try { this.flushReport() } catch (e) {}
    }
    window.addEventListener('beforeunload', this._beforeUnload)
  },
  beforeDestroy () {
    try { this.flushReport() } catch (e) {}
    if (this._beforeUnload) {
      window.removeEventListener('beforeunload', this._beforeUnload)
      this._beforeUnload = null
    }
  },
  // 路由离开时补报
  beforeRouteLeave (to, from, next) {
    try { this.flushReport() } catch (e) {}
    next()
  },
  computed: {
    playerStickyStyle () {
      // 在 App 内不显示标题栏，吸顶到 0；H5 中预留标题栏高度
      const top = this.isApp ? '0px' : '0px'
      return { position: 'sticky', top, zIndex: 12 }
    },
    currentIndex () {
      if (!this.course || !Array.isArray(this.course.courseVideoList)) return -1
      return this.course.courseVideoList.findIndex(v => String(v.videoId) === String(this.currentVideoId))
    },
    hasPrev () { return this.currentIndex > 0 },
    hasNext () {
      const len = (this.course && this.course.courseVideoList) ? this.course.courseVideoList.length : 0
      return this.currentIndex >= 0 && this.currentIndex < len - 1
    },
    // 计算开始学习按钮文案
    detailTitle() {
      return `${this.rehabMerchantConfig.shortName || this.rehabMerchantConfig.name}课程详情`
    },
    startButtonText() {
      if (!this.course) return '开始学习'
      
      // 如果不是VIP课程，直接显示开始学习
      if (!this.course.vip) {
        return '开始学习'
      }
      
      // 如果是VIP课程
      const isLogin = this.$store.state.user.profile.isLogin
      if (!isLogin) {
        return '会员课程，免费试看'  // 未登录用户可以试看
      }
      
      // 已登录但VIP状态未知或未开通
      if (this.userVipStatus === false) {
        return '会员课程，免费试看'  // 非会员可以试看第一个视频
      } else if (this.userVipStatus === true) {
        return '开始学习'
      } else {
        return '检查会员状态...'
      }
    },
    // 计算按钮是否可点击
    startButtonDisabled() {
      // 所有用户都可以点击开始学习（包括试看）
      return false
    },
    // 计算当前是否为试看状态
    isTrialMode() {
      if (!this.course || !this.course.vip) return false
      // 如果是VIP课程且用户不是会员，并且当前播放的是第一个视频
      const isLogin = this.$store.state.user.profile.isLogin
      const hasVip = this.userVipStatus === true
      const isFirstVideo = this.currentVideoId && this.course.courseVideoList && 
                          this.course.courseVideoList.length > 0 && 
                          String(this.course.courseVideoList[0].videoId) === String(this.currentVideoId)
      
      return isFirstVideo && (!isLogin || !hasVip)
    },
    // 计算下一节按钮文案
    nextButtonText() {
      if (this.isTrialMode && this.hasNext) {
        return '开通会员'
      }
      return '下一节'
    },
    // 计算上一节按钮文案  
    prevButtonText() {
      // 试看模式下，如果真的有上一节才显示开通会员，否则显示上一节
      if (this.isTrialMode && this.hasPrev) {
        return '开通会员'
      }
      return '上一节'
    }
  },
  methods: {
    hideLoading () {
      this.$refs.container && this.$refs.container.stopLoading && this.$refs.container.stopLoading()
    },
    hasIntro () {
      return !!(this.course && this.course.intro)
    },
    hasCatalog () {
      return !!(this.course && this.course.courseVideoList && this.course.courseVideoList.length)
    },
    async loadDetail () {
      try {
        this.loading = true
        const data = this.useMarkdownMerchantCourses
          ? getMerchantCoursesFromMarkdown(this.rehabMerchantConfig).find(item => String(item.id) === String(this.courseId) || String(item.courseId) === String(this.courseId))
          : await TicaoDao.findOne(this.courseId)
        console.log('课程详情(findOne):', data)
        this.course = data || {}

        if (this.course && this.course.merchant && this.course.merchant !== this.rehabMerchantState.queryMerchant) {
          this.$store.commit('user/setRehabMerchant', {
            queryMerchant: this.course.merchant,
            key: this.course.merchant
          })
          this.applyRehabMerchantTheme()
        }
        
        // 如果是VIP课程且用户已登录，检查VIP状态
        if (this.course.vip) {
          this.checkUserVipStatus()
        }
        
        // 若带了 videoId，默认选中
        const q = this.$route.query || {}
        const initialVideoId = q.videoId
        if (this.hasCatalog()) {
          if (initialVideoId) {
            const picked = this.course.courseVideoList.find(v => String(v.videoId) === String(initialVideoId))
            if (picked) {
              this.applyCurrentVideo(picked)
            }
          }
        }
      } catch (e) {
        console.error('加载课程详情失败', e)
        this.course = {}
      } finally {
        this.loading = false
      }
    },
    formatDuration (seconds) {
      if (!seconds && seconds !== 0) return '-'
      const s = Number(seconds) || 0
      const mm = Math.floor(s / 60)
      const ss = s % 60
      const pad = (n) => (n < 10 ? '0' + n : '' + n)
      return `${pad(mm)}:${pad(ss)}`
    },
    async goPlay () {
      // 播放第一个视频（所有用户都可以试看第一个视频）
      const first = this.course && this.course.courseVideoList && this.course.courseVideoList[0]
      if (first) {
        this.applyCurrentVideo(first)
        // 自动切换到目录tab
        this.activeTab = 'catalog'
        // 滚动到播放器区域
        this.$nextTick(() => {
          try {
            const el = this.$refs.playerContainer
            if (el && el.scrollIntoView) {
              el.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          } catch (e) {}
        })
      }
    },
    getFirstVideoId () {
      if (this.course && this.course.courseVideoList && this.course.courseVideoList.length) {
        return this.course.courseVideoList[0].videoId
      }
      return undefined
    },
    async selectVideo (v) {
      if (!v) return
      
      // 如果是VIP课程且不是第一个视频，需要检查权限
      if (this.course && this.course.vip && !this.isFirstVideo(v)) {
        const isLogin = this.$store.state.user.profile.isLogin
        if (!isLogin) {
          this.$toast('观看完整课程需要登录并开通会员')
          const query = this.rehabMerchantState.queryMerchant ? `&merchant=${encodeURIComponent(this.rehabMerchantState.queryMerchant)}` : ''
          this.$router.push(`/login/index?returnTo=rehab-courses${query}`)
          return
        }
        const userId = this.$store.state.user.profile.userId
        const ok = await VipDao.isActiveVip(userId)
        if (!ok) {
          // 显示VIP购买弹窗
          this.showVipPopup = true
          return
        }
      }
      
      this.applyCurrentVideo(v)
    },
    applyCurrentVideo (v) {
      this.currentVideoId = v.videoId
      this.currentVideoUrl = v.videoUrl || ''
      this.currentVideoTitle = v.title || ''
      this.currentCoverUrl = v.coverUrl || ''
      // 重置上报游标
      this.lastReportSecond = 0
      this.lastReportTs = 0
    },
    async playPrev () {
      // 如果是试看状态且有上一节，弹出VIP购买弹窗
      if (this.isTrialMode && this.hasPrev) {
        this.handleVipUpgrade()
        return
      }
      
      if (!this.hasPrev) return
      const list = this.course.courseVideoList || []
      const target = list[this.currentIndex - 1]
      if (target) {
        // 如果目标视频需要VIP权限，进行检查
        await this.selectVideo(target)
      }
    },
    async playNext () {
      // 如果是试看状态，直接弹出VIP购买弹窗
      if (this.isTrialMode) {
        this.handleVipUpgrade()
        return
      }
      
      if (!this.hasNext) return
      const list = this.course.courseVideoList || []
      const target = list[this.currentIndex + 1]
      if (target) {
        // 如果目标视频需要VIP权限，进行检查
        await this.selectVideo(target)
      }
    },
    // 播放进度事件（节流上报）
    onTimeUpdate () {
      const el = this.$refs.player
      if (!el) return
      const sec = Math.floor(el.currentTime || 0)
      const now = Date.now()
      const videoId = this.currentVideoId || this.getFirstVideoId()
      if (!this.courseId || !videoId) return
      // 每 reportIntervalSec 秒且进度前进才上报
      if (sec > this.lastReportSecond && (now - this.lastReportTs) >= this.reportIntervalSec * 1000) {
        this.reportWatch(sec)
        this.lastReportSecond = sec
        this.lastReportTs = now
      }
    },
    onEnded () {
      const el = this.$refs.player
      if (!el) return
      const sec = Math.floor(el.duration || el.currentTime || 0)
      this.reportWatch(sec)
    },
    flushReport () {
      const el = this.$refs.player
      if (!el) return
      const sec = Math.floor(el.currentTime || 0)
      if (sec > 0) {
        this.reportWatch(sec)
      }
    },
    async reportWatch (endPosition) {
      try {
        if (this.useMarkdownMerchantCourses) {
          saveMerchantCourseHistory({
            merchantConfig: this.rehabMerchantConfig,
            course: this.course,
            videoId: this.currentVideoId || this.getFirstVideoId(),
            endPosition
          })
          return
        }
        const videoId = this.currentVideoId || this.getFirstVideoId()
        if (!this.courseId || !videoId) return
        // 优先从 store 获取 userId，其次从路由参数兜底
        const userId = (this.$store && this.$store.state && this.$store.state.user && this.$store.state.user.profile && this.$store.state.user.profile.userId)
          || (this.$route && this.$route.query && this.$route.query.userId)
          || undefined
        // 计算距离上次上报的在线时长（单位：秒）
        const nowTs = Date.now()
        const onlineDuration = this.lastReportTs > 0 ? Math.max(0, Math.floor((nowTs - this.lastReportTs) / 1000)) : 0
        const payload = {
          courseId: this.courseId,
          videoId: videoId,
          endPosition: Number(endPosition) || 0,
          onlineDuration
        }
        if (userId) payload.userId = userId
        await TicaoDao.saveWatchHistory(payload)
      } catch (e) {
        // 静默失败
      }
    },
    // 检查用户VIP状态
    async checkUserVipStatus() {
      const isLogin = this.$store.state.user.profile.isLogin
      if (!isLogin) {
        this.userVipStatus = false
        return
      }
      
      try {
        const userId = this.$store.state.user.profile.userId
        const ok = await VipDao.isActiveVip(userId)
        this.userVipStatus = ok
      } catch (error) {
        console.error('检查VIP状态失败:', error)
        this.userVipStatus = false
      }
    },
    // 判断是否是第一个视频
    isFirstVideo(video) {
      if (!this.course || !this.course.courseVideoList || !this.course.courseVideoList.length) {
        return false
      }
      return this.course.courseVideoList[0].videoId === video.videoId
    },
    // 处理VIP升级（统一的VIP引导逻辑）
    async handleVipUpgrade() {
      const isLogin = this.$store.state.user.profile.isLogin
      if (!isLogin) {
        this.$toast('观看完整课程需要登录并开通会员')
        this.$router.push('/login/index?returnTo=rehab-courses')
        return
      }
      
      // 显示VIP购买弹窗
      this.showVipPopup = true
    },
    // VIP购买处理
    onVipPurchase(plan) {
      console.log('用户选择购买VIP套餐:', plan)
      // 这里可以跳转到支付页面或调用支付接口
      this.$toast(`对接中....正在跳转到${plan.name}购买页面...`)
      // TODO: 实现具体的购买逻辑
      // this.$router.push(`/vip/purchase?planId=${plan.id}`)
    }
  }
}
</script>
<style scoped>
.detail { padding-bottom: 80px; }
.detail__cover { height: 200px; background-size: cover; background-position: center; position: relative; display: flex; align-items: flex-end; padding: 12px; color: #fff; }
.detail__cover::after { content: ''; position: absolute; left: 0; right: 0; bottom: 0; height: 60%; background: linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.45) 60%, rgba(0,0,0,0.65) 100%); pointer-events: none; }
.detail__title { position: relative; font-size: 18px; font-weight: 700; z-index: 1; }
.detail__badge { position: absolute; right: 12px; top: 12px; color: #fff; font-size: 12px; padding: 4px 10px; border-radius: 999px; font-weight: 700; letter-spacing: 0.5px; box-shadow: 0 4px 10px rgba(0,0,0,0.15); backdrop-filter: saturate(140%) blur(2px); }
.detail__badge--vip { background: linear-gradient(135deg, #ff8f3f 0%, #ff5b2e 100%); }
.detail__badge--free { background: linear-gradient(135deg, var(--rehab-theme-color, #34d399) 0%, var(--rehab-theme-color-dark, #10b981) 100%); }
.detail__body { background: #fff;  padding: 12px;margin-bottom: 68px;}
.detail__player { background: #000; border-radius: 0; overflow: hidden; line-height: 0; position: relative; }
.detail__player video { display: block; }
.player__title { font-size: 14px; font-weight: 700; margin-top: 8px; color: #333; }
.detail__meta { display: flex; gap: 8px; margin-bottom: 8px; }
.tag { display: inline-flex; align-items: center; padding: 2px 8px; font-size: 12px; line-height: 18px; border-radius: 999px; background: var(--rehab-theme-color-light, #f0fdf4); color: var(--rehab-theme-color-dark, #059669); border: 1px solid color-mix(in srgb, var(--rehab-theme-color, #07c160) 28%, white); font-weight: 600; }
.tag--free { background: color-mix(in srgb, var(--rehab-theme-color-light, #ecfdf5) 82%, white); color: var(--rehab-theme-color, #10b981); border-color: color-mix(in srgb, var(--rehab-theme-color, #10b981) 24%, white); }
.tag--vip { background: #fff7ed; color: #f97316; border-color: #fed7aa; }
.detail__section { margin-top: 8px; }
.detail__section-title { font-size: 14px; font-weight: 700; margin-bottom: 6px; color: var(--rehab-theme-color, #07c160); }
.detail__course-title { font-size: 18px; font-weight: 800; color: #333; margin: 8px 0 6px; }
.detail__brief { color: #666; font-size: 13px; line-height: 1.6; margin-bottom: 8px; }
.detail__text { color: #555; font-size: 14px; line-height: 1.6; white-space: pre-wrap; }
.detail__rich { color: #333; font-size: 14px; line-height: 1.7; }
.detail__rich { overflow: hidden; word-break: break-word; overflow-wrap: anywhere; }
.detail__rich * { max-width: 100% !important; box-sizing: border-box; }
.detail__rich img { width: 100% !important; max-width: 100% !important; height: auto !important; display: block; box-sizing: border-box; }
.detail__rich figure { margin: 0; }
.detail__rich p { margin: 0 0 10px; }
.detail__rich h1, .detail__rich h2, .detail__rich h3 { margin: 12px 0 8px; }
.detail__rich pre, .detail__rich code { white-space: pre-wrap; word-wrap: break-word; }
.detail__rich table { display: block; width: 100% !important; overflow-x: auto; border-collapse: collapse; }
.detail__rich iframe, .detail__rich video { width: 100% !important; height: auto; }
.detail__rich ::v-deep img { width: 100% !important; max-width: 100% !important; height: auto !important; display: block; box-sizing: border-box; }
.detail__rich ::v-deep table { display: block; width: 100% !important; overflow-x: auto; border-collapse: collapse; }
.detail__rich ::v-deep iframe, .detail__rich ::v-deep video { width: 100% !important; height: auto !important; }
.detail__actions { margin-top: 12px; }
.detail__bottom-bar { position: fixed; left: 0; right: 0; bottom: 0; z-index: 100; background: #fff; box-shadow: 0 -2px 10px rgba(0,0,0,0.06); display: flex; align-items: center; justify-content: space-between; padding: 12px; min-height: 64px; }
.bottom-bar__left { min-width: 0; }
.bottom-bar__title { font-size: 13px; color: #07c160; font-weight: 700; }
.bottom-bar__desc { font-size: 12px; color: #888; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 64vw; }
.bottom-bar__right .van-button { padding: 0 16px; }
.bottom-bar__nav { display: flex; gap: 12px; }
.nav-btn { height: 44px; font-size: 18px; padding: 0 20px; }
.nav-btn--primary { font-weight: 700; }
.catalog { display: grid; grid-template-columns: 1fr; gap: 8px; }
.catalog__item { 
  background: #f8f8f8; 
  border-radius: 8px; 
  padding: 8px 10px; 
  cursor: pointer;
  transition: all 0.2s ease;
}
.catalog__item:hover { background: color-mix(in srgb, var(--rehab-theme-color-light, #f0f0f0) 70%, white); }
.catalog__item.is-unlocked { border: 1px solid #f59e0b; background: #fffbeb; }
.catalog__item.is-trial { border: 1px solid var(--rehab-theme-color, #10b981); background: color-mix(in srgb, var(--rehab-theme-color-light, #ecfdf5) 82%, white); }
.catalog__item.is-locked { 
  background: #f9f9f9; 
  opacity: 0.7;
}
.catalog__item.is-locked:hover { background: #f5f5f5; }
/* 当前播放状态优先级最高 */
.catalog__item.is-active { 
  border: 1px solid var(--rehab-theme-color, #07c160) !important; 
  background: color-mix(in srgb, var(--rehab-theme-color-light, #f0fff7) 86%, white) !important; 
}

.catalog__content { 
  display: flex; 
  flex-direction: column; 
  gap: 2px; 
}

.catalog__title { 
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
}

.catalog__title-text {
  font-size: 14px; 
  font-weight: 600; 
  color: #333;
  flex: 1;
  line-height: 1.4;
}

.catalog__badges {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.catalog__item.is-locked .catalog__title-text { color: #999; }

.catalog__badge {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 700;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.catalog__badge--trial {
  background: linear-gradient(135deg, var(--rehab-theme-color, #10b981) 0%, var(--rehab-theme-color-dark, #059669) 100%);
  color: #fff;
  box-shadow: 0 1px 3px color-mix(in srgb, var(--rehab-theme-color, #10b981) 30%, transparent);
}

.catalog__badge--unlocked {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: #fff;
  box-shadow: 0 1px 3px rgba(245, 158, 11, 0.3);
}

.catalog__lock {
  flex-shrink: 0;
}

.catalog__meta { 
  font-size: 12px; 
  color: #888; 
  margin-top: 2px; 
}
</style>


